import { useCallback, useEffect, useState } from 'react';
import { formatearMonedaArs, type ReporteFacturacionPortalDTO } from '@ausentia/shared';
import { api, ErrorApi } from '../api/cliente.js';

function mesActual(): { desde: string; hasta: string } {
  const hoy = new Date();
  const primero = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
  const ultimo = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0);
  const iso = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { desde: iso(primero), hasta: iso(ultimo) };
}

function obtenerFecha(isoString?: string | null): string {
  if (!isoString) return '—';
  try {
    if (isoString.includes('-') && !isoString.includes('T')) {
      const [y, m, d] = isoString.split('-');
      return `${d}/${m}/${y}`;
    }
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return '—';
  }
}

export function FacturacionPortal() {
  const inicial = mesActual();
  const [desde, setDesde] = useState(inicial.desde);
  const [hasta, setHasta] = useState(inicial.hasta);
  const [reporte, setReporte] = useState<ReporteFacturacionPortalDTO | null>(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');

  const calcular = useCallback(async () => {
    setCargando(true);
    setError('');
    try {
      setReporte(await api.get<ReporteFacturacionPortalDTO>(`/api/portal/facturacion?desde=${desde}&hasta=${hasta}`));
    } catch (err) {
      setError(err instanceof ErrorApi ? err.message : 'No se pudo cargar la facturación');
      setReporte(null);
    } finally {
      setCargando(false);
    }
  }, [desde, hasta]);

  useEffect(() => {
    void calcular();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section style={{ animation: 'fadeInUp 0.3s ease forwards' }}>
      <div className="fila" style={{ marginBottom: 20, alignItems: 'center' }}>
        <div>
          <h2>Liquidación y Costos del Período</h2>
          <p className="muted" style={{ margin: '4px 0 0' }}>
            Consulte el total acumulado a pagar y el detalle de controles médicos laborales realizados para su personal.
          </p>
        </div>
      </div>

      <div className="filtro-periodo">
        <div className="campo">
          <label htmlFor="desde">Desde</label>
          <input id="desde" type="date" value={desde} onChange={(e) => setDesde(e.target.value)} />
        </div>
        <div className="campo">
          <label htmlFor="hasta">Hasta</label>
          <input id="hasta" type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} />
        </div>
        <button className="primario" style={{ width: 'auto' }} onClick={() => void calcular()} disabled={cargando}>
          {cargando ? 'Calculando…' : 'Buscar'}
        </button>
      </div>

      {error && <div className="error-box" role="status">{error}</div>}
      {cargando && !reporte && <div className="cargando">Cargando liquidación…</div>}

      {reporte && (
        <>
          <div className="grid-tarjetas" style={{ marginTop: 18, marginBottom: 20 }}>
            <div className="kpi" style={{ borderLeft: '4px solid var(--verde)' }}>
              <span className="kpi-rotulo">Total a Pagar</span>
              <span className="kpi-monto">{formatearMonedaArs(reporte.resumen.totalACobrarCentavos)}</span>
              <span className="mini muted" style={{ marginTop: 4 }}>Visitas facturadas en el período</span>
            </div>
            <div className="kpi">
              <span className="kpi-rotulo">Controles Realizados</span>
              <span className="kpi-monto" style={{ fontSize: 24, fontWeight: 'bold' }}>{reporte.resumen.totalControles}</span>
              <span className="mini muted" style={{ marginTop: 4 }}>Auditorías completadas</span>
            </div>
          </div>

          {reporte.resumen.totalSinTarifa > 0 && (
            <div className="aviso-info" style={{ marginBottom: 20 }}>
              <span>⚠️</span>
              <span>
                Hay {reporte.resumen.totalSinTarifa} visita(s) que no tienen cargada la tarifa de convenio en el sistema. 
                Por favor, contacte a soporte para registrar los valores.
              </span>
            </div>
          )}

          <h3 style={{ marginTop: 24, marginBottom: 12 }}>Detalle de Auditorías Médicas</h3>
          {reporte.visitas.length === 0 ? (
            <p className="muted">No se registran visitas de control en el período seleccionado.</p>
          ) : (
            <div className="tabla-contenedor">
              <table className="tabla">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Trabajador</th>
                    <th>Tipo de Control</th>
                    <th>Constancia</th>
                    <th>Costo</th>
                  </tr>
                </thead>
                <tbody>
                  {reporte.visitas.map((v) => (
                    <tr key={v.controlId}>
                      <td>{obtenerFecha(v.fechaControl)}</td>
                      <td>
                        <strong>{v.trabajadorNombre}</strong>
                        <div className="mini">DNI {v.trabajadorDni} {v.trabajadorLegajo ? `· Legajo ${v.trabajadorLegajo}` : ''}</div>
                      </td>
                      <td>{v.tipoControlNombre}</td>
                      <td>
                        {v.constanciaCodigo ? (
                          <span className="mini" style={{ fontFamily: 'monospace' }}>{v.constanciaCodigo}</span>
                        ) : '—'}
                      </td>
                      <td>
                        {v.tieneTarifa && v.montoCentavos !== null ? (
                          <strong>{formatearMonedaArs(v.montoCentavos)}</strong>
                        ) : (
                          <span className="insignia roja">Sin tarifa de convenio</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
}
